import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { devisAPI, usersAPI, emailTemplatesAPI, clientsAPI, miniPellesAPI, uploadAPI, devisAssignmentAPI, tasksAPI, aiAPI, financingAPI, piecesRequisesAPI } from '@/lib/api';
import { formatPrice, formatDate, formatDateTime, getStatusColor, getStatusLabel, getActivityIcon, cn, getImageUrl } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import EntityChat from '@/components/EntityChat';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  Phone,
  MessageSquare,
  Mail,
  StickyNote,
  Printer,
  Trash2,
  UserCog,
  Loader2,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  UserPlus,
  Package,
  Wrench,
  Building2,
  Calculator,
  FileText,
  Download,
  Pencil,
  Copy,
  Archive,
  RefreshCw,
  X,
  CheckSquare,
  Calendar,
  MapPin,
  Sparkles,
  Zap,
  User,
  Upload,
  FolderOpen,
  Check,
  AlertCircle,
  Banknote,
  Plus,
  ExternalLink,
  Wallet,
  Target,
} from 'lucide-react';
import { ServiceQuoteDetailSections } from '@/pages/ServiceQuoteShared';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Company Info
const COMPANY_NAME = "IDMAT OI";
const COMPANY_ADDRESS = "27 avenue des artisans";
const COMPANY_CITY = "97436 Saint Leu";
const COMPANY_SIRET = "99536928900011";
const COMPANY_EMAIL = "contact@idmatoi.com";
const COMPANY_PHONE = "0693526449";
const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_compact-crm-3/artifacts/4rl6gmml_Logo%20V4.png";

const relanceResponseConfig = {
  interested: { label: 'Client intéressé', className: 'bg-green-100 text-green-700' },
  on_hold: { label: 'En attente client', className: 'bg-amber-100 text-amber-700' },
  not_interested: { label: 'Client non intéressé', className: 'bg-slate-200 text-slate-700' },
};

const DevisDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const printRef = useRef();
  
  const [devis, setDevis] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Navigation entre devis
  const [allDevisIds, setAllDevisIds] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [activityType, setActivityType] = useState('note');
  const [activityContent, setActivityContent] = useState('');
  const [activityLoading, setActivityLoading] = useState(false);
  
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [newUserId, setNewUserId] = useState('');
  const [reassignLoading, setReassignLoading] = useState(false);
  
  // Assign vendeur dialog (for new devis)
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignVendeurId, setAssignVendeurId] = useState('');
  const [assignLoading, setAssignLoading] = useState(false);
  
  // Email dialog
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailCcList, setEmailCcList] = useState([]);
  const [emailCcInput, setEmailCcInput] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [includeBrochure, setIncludeBrochure] = useState(false);
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [includeFinancingDocs, setIncludeFinancingDocs] = useState(false);
  const [selectedFinancingDocs, setSelectedFinancingDocs] = useState([]);
  const [includePiecesList, setIncludePiecesList] = useState(false);
  const [piecesRequises, setPiecesRequises] = useState([]);
  const attachmentInputRef = React.useRef(null);
  
  // Relance dialog
  const [relanceDialogOpen, setRelanceDialogOpen] = useState(false);
  const [relanceEmail, setRelanceEmail] = useState('');
  const [relanceMessage, setRelanceMessage] = useState('');
  const [relanceCcList, setRelanceCcList] = useState([]);
  const [relanceCcInput, setRelanceCcInput] = useState('');
  const [relanceLoading, setRelanceLoading] = useState(false);
  
  // Task dialog
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskTitle, setTaskTitle] = useState('');
  const [taskType, setTaskType] = useState('rappel');
  const [taskPriority, setTaskPriority] = useState('normale');
  const [taskDueDate, setTaskDueDate] = useState('');
  const [taskDescription, setTaskDescription] = useState('');
  const [taskLoading, setTaskLoading] = useState(false);
  const [devisTasks, setDevisTasks] = useState([]);
  const [syncingToOdoo, setSyncingToOdoo] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  
  // PDF download
  const [pdfLoading, setPdfLoading] = useState(false);
  
  // Client info
  const [clientInfo, setClientInfo] = useState(null);
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [clientEditMode, setClientEditMode] = useState(false);
  const [clientForm, setClientForm] = useState({});
  const [savingClient, setSavingClient] = useState(false);
  
  // AI Note suggestions
  const [noteSuggestions, setNoteSuggestions] = useState([]);
  const [analyzingNote, setAnalyzingNote] = useState(false);
  
  // Apport client pour simulation SNC (défaut 10%)
  const [apportClientPercent, setApportClientPercent] = useState(30);
  
  // Financing Request states
  const [financingRequest, setFinancingRequest] = useState(null);
  const [financingLoading, setFinancingLoading] = useState(false);
  const [uploadingDocId, setUploadingDocId] = useState(null);
  const fileInputRefs = useRef({});

  useEffect(() => {
    fetchData();
    fetchAllDevisIds();
  }, [id]);

  // Sync currentIndex when devis is loaded via ObjectId (old notification links)
  useEffect(() => {
    if (devis?.id && allDevisIds.length > 0 && currentIndex === -1) {
      const idx = allDevisIds.indexOf(devis.id);
      if (idx !== -1) setCurrentIndex(idx);
    }
  }, [devis, allDevisIds]);

  const fetchData = async () => {
    try {
      const [devisRes, usersRes] = await Promise.all([
        devisAPI.getOne(id),
        isAdmin() ? usersAPI.getAll() : Promise.resolve({ data: [] }),
      ]);
      setDevis(devisRes.data);
      setUsers(usersRes.data);
      
      // Load client info
      if (devisRes.data?.client_id) {
        try {
          const clientRes = await clientsAPI.getOne(devisRes.data.client_id);
          setClientInfo(clientRes.data);
        } catch (e) {
          console.error('Failed to load client info');
        }
      }
      
      // Load financing request if exists
      if (devisRes.data?.financing_request_id) {
        try {
          const financingRes = await financingAPI.getRequest(devisRes.data.financing_request_id);
          setFinancingRequest(financingRes.data);
        } catch (e) {
          console.error('Failed to load financing request');
        }
      }
      
      // Load tasks linked to this devis
      try {
        const tasksRes = await tasksAPI.getAll();
        const linkedTasks = tasksRes.data.filter(t => t.devis_id === id);
        setDevisTasks(linkedTasks);
      } catch (e) {
        console.error('Failed to load tasks');
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
      navigate('/admin/devis');
    } finally {
      setLoading(false);
    }
  };

  // Charger la liste complète des IDs pour la navigation
  const fetchAllDevisIds = async () => {
    try {
      const res = await devisAPI.getAll();
      const items = Array.isArray(res.data) ? res.data : (res.data?.devis || res.data?.items || []);
      const ids = items.map(d => d.id).filter(Boolean);
      setAllDevisIds(ids);
      const idx = ids.indexOf(id);
      setCurrentIndex(idx);
    } catch (e) { console.error(e); }
  };

  const navigateToDevis = (direction) => {
    const newIndex = currentIndex + direction;
    if (newIndex >= 0 && newIndex < allDevisIds.length) {
      navigate(`/admin/devis/${allDevisIds[newIndex]}`);
    }
  };

  const handleAssignVendeur = async () => {
    if (!assignVendeurId) {
      toast.error('Veuillez sélectionner un vendeur');
      return;
    }
    
    setAssignLoading(true);
    try {
      await devisAssignmentAPI.assign(id, assignVendeurId);
      toast.success('Devis assigné avec succès. Un email a été envoyé au vendeur.');
      setAssignDialogOpen(false);
      setAssignVendeurId('');
      fetchData(); // Refresh
    } catch (error) {
      toast.error("Erreur lors de l'assignation");
    } finally {
      setAssignLoading(false);
    }
  };

  const handleSyncToOdoo = async () => {
    setSyncingToOdoo(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/devis/${devis.id}/sync-odoo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (res.ok) {
        const data = await res.json();
        setDevis(prev => ({ ...prev, odoo_so_id: data.odoo_so_id }));
        window.open(`https://erp.ma-grp.fr/odoo/sales/${data.odoo_so_id}`, '_blank');
      } else {
        alert('Erreur lors de la synchronisation Odoo');
      }
    } catch (e) {
      alert('Erreur réseau');
    } finally {
      setSyncingToOdoo(false);
    }
  };

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/devis/${id}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Erreur PDF');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `devis_${devis.reference}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('PDF téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailTo.trim()) {
      toast.error('Veuillez saisir une adresse email');
      return;
    }
    
    setEmailLoading(true);
    try {
      // Upload additional attachments first
      const uploadedUrls = [];
      for (const file of attachmentFiles) {
        try {
          const formData = new FormData();
          formData.append('file', file);
          
          // Use appropriate upload endpoint based on file type
          let response;
          if (file.type === 'application/pdf') {
            response = await uploadAPI.uploadPDF(formData);
          } else {
            response = await uploadAPI.uploadImage(file);
          }
          
          if (response.data?.url) {
            uploadedUrls.push(response.data.url);
          }
        } catch (uploadError) {
          console.error(`Failed to upload ${file.name}:`, uploadError);
          toast.error(`Erreur lors de l'upload de ${file.name}`);
        }
      }
      
      // Add financing documents URLs if selected
      const financingDocUrls = [];
      if (includeFinancingDocs && financingRequest?.documents) {
        for (const doc of financingRequest.documents) {
          if (selectedFinancingDocs.includes(doc.doc_id) && doc.file_path) {
            // The file_path is a local path, we need to use the download URL
            const docUrl = `${process.env.REACT_APP_BACKEND_URL}/api/financing/requests/${financingRequest.id}/download/${doc.doc_id}`;
            financingDocUrls.push(docUrl);
          }
        }
      }
      
      await devisAPI.sendEmail(id, { 
        email: emailTo, 
        message: emailMessage,
        cc_emails: emailCcList.filter(e => e.trim()),
        include_brochure: includeBrochure,
        additional_attachments: [...uploadedUrls, ...financingDocUrls],
        financing_request_id: includeFinancingDocs ? financingRequest?.id : null,
        financing_doc_ids: includeFinancingDocs ? selectedFinancingDocs : [],
        include_pieces_list: includePiecesList,
        pieces_requises: includePiecesList ? piecesRequises.map(p => p.name) : [],
      });
      const ccInfo = emailCcList.length > 0 ? ` (CC: ${emailCcList.join(', ')})` : '';
      toast.success(`Devis envoyé à ${emailTo}${ccInfo}`);
      setEmailDialogOpen(false);
      setEmailTo('');
      setEmailMessage('');
      setEmailCcList([]);
      setIncludeBrochure(false);
      setAttachmentFiles([]);
      setIncludeFinancingDocs(false);
      setSelectedFinancingDocs([]);
      setIncludePiecesList(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setEmailLoading(false);
    }
  };

  const initializeEmailDialog = async () => {
    // Pre-fill email with client email
    if (devis?.client_id) {
      try {
        const clientRes = await clientsAPI.getOne(devis.client_id);
        if (clientRes.data?.email) {
          setEmailTo(clientRes.data.email);
        }
      } catch (error) {
        console.error('Failed to load client email');
      }
    }
    
    // Pre-fill CC with cabinet SNC email and banques if present
    const ccEmails = [];
    if (devis?.cabinet_snc_email) {
      ccEmails.push(devis.cabinet_snc_email);
    }
    // Add banques emails to CC
    if (devis?.banques && Array.isArray(devis.banques)) {
      devis.banques.forEach(banque => {
        if (banque.email && !ccEmails.includes(banque.email)) {
          ccEmails.push(banque.email);
        }
      });
    }
    setEmailCcList(ccEmails);
    setSelectedTemplateId('');
    setEmailMessage('');
    setIncludeBrochure(false);
    setAttachmentFiles([]);
    setIncludeFinancingDocs(false);
    setIncludePiecesList(false);
    
    // Pre-select financing docs that are uploaded/validated
    if (financingRequest?.documents) {
      const uploadedDocs = financingRequest.documents
        .filter(doc => doc.status === 'uploaded' || doc.status === 'validated')
        .map(doc => doc.doc_id);
      setSelectedFinancingDocs(uploadedDocs);
    } else {
      setSelectedFinancingDocs([]);
    }
    
    // Load email templates
    try {
      const response = await emailTemplatesAPI.getAll();
      setEmailTemplates(response.data);
    } catch (error) {
      console.error('Failed to load email templates');
    }
    
    // Load pieces requises based on client type
    try {
      const clientType = clientInfo?.client_type || 'particulier';
      const piecesRes = await piecesRequisesAPI.getByClientType(clientType, {
        for_financement: devis?.financement_type && devis.financement_type !== 'comptant',
        for_snc: devis?.cabinet_snc_id ? true : false
      });
      setPiecesRequises(piecesRes.data);
    } catch (error) {
      console.error('Failed to load pieces requises');
      setPiecesRequises([]);
    }
    
    setEmailDialogOpen(true);
  };

  // Check for brochure - either in devis specs or fetch from mini_pelle
  const [hasBrochure, setHasBrochure] = useState(false);
  const [brochureUrl, setBrochureUrl] = useState(null);

  useEffect(() => {
    const checkBrochure = async () => {
      // First check in devis specs
      const specsUrl = devis?.mini_pelle_specs?.brochure_url;
      if (specsUrl) {
        setHasBrochure(true);
        setBrochureUrl(specsUrl);
        return;
      }
      
      // If not in specs, fetch from mini_pelle
      if (devis?.mini_pelle_id) {
        try {
          const response = await miniPellesAPI.getOne(devis.mini_pelle_id);
          if (response.data?.brochure_url) {
            setHasBrochure(true);
            setBrochureUrl(response.data.brochure_url);
          } else {
            setHasBrochure(false);
            setBrochureUrl(null);
          }
        } catch (error) {
          console.error('Error fetching mini pelle for brochure:', error);
          setHasBrochure(false);
        }
      }
    };
    
    if (devis) {
      checkBrochure();
    }
  }, [devis]);

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplateId(templateId);
    if (templateId && templateId !== 'none') {
      const template = emailTemplates.find(t => t.id === templateId);
      if (template) {
        setEmailMessage(template.content);
      }
    } else {
      setEmailMessage('');
    }
  };

  const addCcEmail = () => {
    if (emailCcInput.trim() && !emailCcList.includes(emailCcInput.trim())) {
      setEmailCcList([...emailCcList, emailCcInput.trim()]);
      setEmailCcInput('');
    }
  };

  const removeCcEmail = (email) => {
    setEmailCcList(emailCcList.filter(e => e !== email));
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await devisAPI.update(id, { status: newStatus });
      toast.success('Statut mis à jour');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDuplicate = async () => {
    try {
      const response = await devisAPI.duplicate(id);
      toast.success(`Devis dupliqué: ${response.data.reference}`);
      navigate(`/admin/devis/${response.data.id}`);
    } catch (error) {
      toast.error('Erreur lors de la duplication');
    }
  };

  const handleArchive = async () => {
    try {
      await devisAPI.archive(id);
      toast.success('Devis archivé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'archivage');
    }
  };

  const handleAddActivity = async () => {
    if (!activityContent.trim()) return;
    
    setActivityLoading(true);
    try {
      await devisAPI.addActivity(id, { type: activityType, content: activityContent });
      toast.success('Activité ajoutée');
      
      // Analyze ALL activity types for task suggestions (note, appel, sms, mail)
      setAnalyzingNote(true);
      try {
        const analysisRes = await aiAPI.analyzeNote({
          note_content: activityContent,
          devis_id: devis?.id,
          client_name: devis?.client_name,
          devis_reference: devis?.reference
        });
        
        if (analysisRes.data?.suggestions?.length > 0) {
          setNoteSuggestions(analysisRes.data.suggestions);
        }
      } catch (e) {
        console.error('Failed to analyze note:', e);
      } finally {
        setAnalyzingNote(false);
      }
      
      setActivityContent('');
      setActivityDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    } finally {
      setActivityLoading(false);
    }
  };
  
  const handleCreateTaskFromSuggestion = async (suggestion) => {
    try {
      await aiAPI.createTaskFromNote({
        ...suggestion,
        client_id: devis?.client_id
      });
      toast.success(`Tâche "${suggestion.title}" créée pour le ${suggestion.suggested_date}`);
      setNoteSuggestions(prev => prev.filter(s => s.title !== suggestion.title));
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la création de la tâche');
    }
  };
  
  const dismissSuggestion = (suggestion) => {
    setNoteSuggestions(prev => prev.filter(s => s.title !== suggestion.title));
  };

  const handleReassign = async () => {
    if (!newUserId) return;
    
    setReassignLoading(true);
    try {
      await devisAPI.reassign(id, { new_user_id: newUserId });
      toast.success('Devis réassigné');
      setReassignDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la réassignation');
    } finally {
      setReassignLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer ce devis ?')) return;
    
    try {
      await devisAPI.delete(id);
      toast.success('Devis supprimé');
      navigate('/admin/devis');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleCreateTask = async () => {
    if (!taskTitle.trim() || !taskDueDate) {
      toast.error('Titre et date d\'échéance requis');
      return;
    }
    
    setTaskLoading(true);
    try {
      if (editingTask) {
        // Mode édition
        await tasksAPI.update(editingTask.id, {
          title: taskTitle,
          description: taskDescription,
          task_type: taskType,
          priority: taskPriority,
          due_date: taskDueDate + 'T09:00:00',
        });
        toast.success('Tâche mise à jour');
        setEditingTask(null);
      } else {
        // Mode création
        await tasksAPI.create({
          title: taskTitle,
          description: taskDescription,
          task_type: taskType,
          priority: taskPriority,
          due_date: taskDueDate + 'T09:00:00',
          devis_id: devis.id,
          devis_reference: devis.reference,
          client_id: devis.client_id,
          client_name: devis.client_name,
        });
        toast.success('Tâche créée');
      }
      setTaskDialogOpen(false);
      setTaskTitle('');
      setTaskDescription('');
      setTaskType('rappel');
      setTaskPriority('normale');
      setTaskDueDate('');
      fetchData(); // Refresh to show new activity
    } catch (error) {
      toast.error('Erreur lors de la création de la tâche');
    } finally {
      setTaskLoading(false);
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await tasksAPI.complete(taskId);
      toast.success('Tâche terminée et archivée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setTaskTitle(task.title || '');
    setTaskDescription(task.description || '');
    setTaskType(task.task_type || 'rappel');
    setTaskPriority(task.priority || 'normale');
    setTaskDueDate(task.due_date?.split('T')[0] || '');
    setTaskDialogOpen(true);
  };

  const handlePrint = () => {
    const printContent = document.getElementById('print-content');
    const originalContents = document.body.innerHTML;
    
    document.body.innerHTML = printContent.innerHTML;
    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload();
  };

  // ============= FINANCING FUNCTIONS =============
  
  const handleCreateFinancingRequest = async () => {
    if (!devis?.client_id) {
      toast.error('Client non défini pour ce devis');
      return;
    }
    
    setFinancingLoading(true);
    try {
      // Get client type from client info
      const clientType = clientInfo?.category || 'particulier';
      
      const response = await financingAPI.createRequest({
        client_id: devis.client_id,
        devis_ids: [devis.id],
        client_type: clientType,
        amount: devis.total || 0,
      });
      
      setFinancingRequest(response.data);
      toast.success('Demande de financement créée');
      fetchData(); // Refresh devis to get financing_request_id
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setFinancingLoading(false);
    }
  };

  const handleUploadFinancingDoc = async (docId, file) => {
    if (!financingRequest?.id) return;
    
    setUploadingDocId(docId);
    try {
      await financingAPI.uploadDocument(financingRequest.id, docId, file);
      toast.success('Document téléversé');
      
      // Refresh financing request
      const response = await financingAPI.getRequest(financingRequest.id);
      setFinancingRequest(response.data);
    } catch (error) {
      toast.error("Erreur lors du téléversement");
    } finally {
      setUploadingDocId(null);
    }
  };

  const handleValidateFinancingDoc = async (docId) => {
    if (!financingRequest?.id) return;
    
    try {
      await financingAPI.validateDocument(financingRequest.id, docId);
      toast.success('Document validé');
      
      const response = await financingAPI.getRequest(financingRequest.id);
      setFinancingRequest(response.data);
    } catch (error) {
      toast.error('Erreur lors de la validation');
    }
  };

  const handleRejectFinancingDoc = async (docId) => {
    if (!financingRequest?.id) return;
    
    const reason = prompt('Raison du rejet :');
    if (reason === null) return;
    
    try {
      await financingAPI.rejectDocument(financingRequest.id, docId, reason);
      toast.success('Document rejeté');
      
      const response = await financingAPI.getRequest(financingRequest.id);
      setFinancingRequest(response.data);
    } catch (error) {
      toast.error('Erreur lors du rejet');
    }
  };

  const handleUpdateFinancingStatus = async (newStatus) => {
    if (!financingRequest?.id) return;
    
    try {
      await financingAPI.updateRequest(financingRequest.id, { status: newStatus });
      toast.success('Statut mis à jour');
      
      const response = await financingAPI.getRequest(financingRequest.id);
      setFinancingRequest(response.data);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const getFinancingStatusInfo = (status) => {
    const statuses = {
      en_cours: { name: 'En cours', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
      dossier_complet: { name: 'Dossier complet', color: 'bg-blue-100 text-blue-700', icon: FolderOpen },
      envoye_banque: { name: 'Envoyé banque', color: 'bg-purple-100 text-purple-700', icon: Send },
      accepte: { name: 'Accepté', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      refuse: { name: 'Refusé', color: 'bg-red-100 text-red-700', icon: XCircle },
    };
    return statuses[status] || statuses.en_cours;
  };

  const getDocStatusColor = (status) => {
    const colors = {
      pending: 'bg-slate-100 text-slate-500 border-slate-200',
      uploaded: 'bg-blue-100 text-blue-700 border-blue-200',
      validated: 'bg-green-100 text-green-700 border-green-200',
      rejected: 'bg-red-100 text-red-700 border-red-200',
    };
    return colors[status] || colors.pending;
  };

  const getDocStatusLabel = (status) => {
    const labels = {
      pending: 'En attente',
      uploaded: 'Téléversé',
      validated: 'Validé',
      rejected: 'Rejeté',
    };
    return labels[status] || 'En attente';
  };

  // Check if relance is possible based on current status
  const canRelance = ['envoye', 'relance_1', 'relance_2', 'relance_3'].includes(devis?.status);
  
  const getRelanceLevel = () => {
    const statusMap = {
      'envoye': 1,
      'relance_1': 2,
      'relance_2': 3,
      'relance_3': 4,
    };
    return statusMap[devis?.status] || 0;
  };

  const initializeRelanceDialog = async () => {
    // Pre-fill email with client email
    if (devis?.client_id) {
      try {
        const clientRes = await clientsAPI.getOne(devis.client_id);
        if (clientRes.data?.email) {
          setRelanceEmail(clientRes.data.email);
        }
      } catch (error) {
        console.error('Failed to load client email');
      }
    }
    
    // Pas de CC auto sur les relances (SNC et banques retirés)
    setRelanceCcList([]);
    setRelanceMessage('');
    setRelanceDialogOpen(true);
  };

  const addRelanceCc = () => {
    if (relanceCcInput.trim() && !relanceCcList.includes(relanceCcInput.trim())) {
      setRelanceCcList([...relanceCcList, relanceCcInput.trim()]);
      setRelanceCcInput('');
    }
  };

  const removeRelanceCc = (email) => {
    setRelanceCcList(relanceCcList.filter(e => e !== email));
  };

  const handleSendRelance = async () => {
    if (!relanceEmail.trim()) {
      toast.error('Veuillez saisir une adresse email');
      return;
    }
    
    setRelanceLoading(true);
    try {
      const response = await devisAPI.sendRelance(id, { 
        email: relanceEmail, 
        message: relanceMessage || null,
        cc_emails: relanceCcList.filter(e => e.trim()),
      });
      toast.success(response.data.message);
      setRelanceDialogOpen(false);
      setRelanceEmail('');
      setRelanceMessage('');
      setRelanceCcList([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi de la relance');
    } finally {
      setRelanceLoading(false);
    }
  };

  // Client edit functions
  const handleOpenClientDialog = () => {
    if (clientInfo) {
      setClientForm({
        name: clientInfo.name || '',
        email: clientInfo.email || '',
        phone: clientInfo.phone || '',
        address: clientInfo.address || '',
        postal_code: clientInfo.postal_code || '',
        city: clientInfo.city || '',
        company: clientInfo.company || '',
        notes: clientInfo.notes || '',
        category: clientInfo.category || '',
      });
    }
    setClientDialogOpen(true);
    setClientEditMode(false);
  };

  const handleSaveClient = async () => {
    if (!devis?.client_id) return;
    
    // Validation: Catégorie obligatoire
    if (!clientForm.category) {
      toast.error('Veuillez sélectionner une catégorie pour le client');
      return;
    }
    
    setSavingClient(true);
    try {
      await clientsAPI.update(devis.client_id, clientForm);
      toast.success('Client mis à jour');
      setClientEditMode(false);
      // Refresh client info
      const clientRes = await clientsAPI.getById(devis.client_id);
      setClientInfo(clientRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSavingClient(false);
    }
  };

  // Export contact to vCard (for Mac/iPhone)
  const handleExportVCard = () => {
    if (!clientInfo) {
      toast.error('Informations client non disponibles');
      return;
    }

    // Create vCard content (version 3.0 for best compatibility)
    const vCardLines = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      `FN:${clientInfo.name || devis.client_name}`,
      `N:${clientInfo.name?.split(' ').reverse().join(';') || devis.client_name};;;`,
    ];

    if (clientInfo.company) {
      vCardLines.push(`ORG:${clientInfo.company}`);
    }

    if (clientInfo.email) {
      vCardLines.push(`EMAIL;TYPE=INTERNET:${clientInfo.email}`);
    }

    if (clientInfo.phone) {
      // Format phone number for vCard
      vCardLines.push(`TEL;TYPE=CELL:${clientInfo.phone}`);
    }

    if (clientInfo.address || clientInfo.city) {
      const adrParts = [
        '', // PO Box
        '', // Extended address
        clientInfo.address || '', // Street
        clientInfo.city || '', // City
        '', // Region
        clientInfo.postal_code || '', // Postal code
        'France' // Country
      ];
      vCardLines.push(`ADR;TYPE=WORK:${adrParts.join(';')}`);
    }

    // Add note with devis reference
    vCardLines.push(`NOTE:Client IDMAT OI - Devis ${devis.reference}`);

    vCardLines.push('END:VCARD');

    const vCardContent = vCardLines.join('\r\n');

    // Create and download the file
    const blob = new Blob([vCardContent], { type: 'text/vcard;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(clientInfo.name || devis.client_name).replace(/[^a-zA-Z0-9]/g, '_')}.vcf`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    toast.success('Contact exporté ! Ouvrez le fichier .vcf pour l\'ajouter à vos contacts.');
  };

  const getActivityIconComponent = (type) => {
    const icons = {
      appel: Phone,
      sms: MessageSquare,
      mail: Mail,
      note: StickyNote,
    };
    return icons[type] || Clock;
  };

  // Calculs simulation SNC avec apport client
  const calculateSNC = (total, apportClientPct = 30) => {
    const apportSNC = total * 0.30; // Apport SNC fixe 30%
    const apportClient = total * (apportClientPct / 100); // Apport client variable
    const totalApport = apportSNC + apportClient;
    const totalFinance = total - totalApport;
    const loyerMensuel = (totalFinance / 60) * 1.2;
    return { apportSNC, apportClient, totalApport, totalFinance, loyerMensuel };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-800"></div>
      </div>
    );
  }

  if (!devis) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Devis non trouvé</p>
        <Link to="/admin/devis">
          <Button variant="link">Retour aux devis</Button>
        </Link>
      </div>
    );
  }

  const snc = calculateSNC(devis.total, apportClientPercent);
  const relanceResponse = relanceResponseConfig[devis?.client_relance_state];

  return (
    <div className="space-y-6" data-testid="devis-detail-page">
      {/* Mobile Edit Button - Centered */}
      <div className="sm:hidden flex justify-center -mb-4">
        <Link to={`/admin/devis/${id}/modifier`}>
          <Button className="bg-blue-600 hover:bg-blue-700 text-white">
            <Pencil className="w-4 h-4 mr-2" />
            Modifier le devis
          </Button>
        </Link>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 no-print">
        <div className="flex items-center gap-2">
          <Link to="/admin/devis">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>

          {/* Flèches navigation devis */}
          {allDevisIds.length > 1 && (
            <div className="flex items-center gap-1 border border-slate-200 rounded-lg overflow-hidden">
              <button
                onClick={() => navigateToDevis(-1)}
                disabled={currentIndex <= 0}
                data-testid="prev-devis-btn"
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors border-r border-slate-200"
                title="Devis précédent"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-2 text-xs text-slate-400 font-mono">
                {currentIndex + 1}/{allDevisIds.length}
              </span>
              <button
                onClick={() => navigateToDevis(1)}
                disabled={currentIndex >= allDevisIds.length - 1}
                data-testid="next-devis-btn"
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors border-l border-slate-200"
                title="Devis suivant"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}

          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-slate-800 font-mono">
                {devis.reference}
              </h1>
              <Badge className={getStatusColor(devis.status)}>
                {getStatusLabel(devis.status)}
              </Badge>
            </div>
            <p className="text-slate-500 mt-1">
              Créé le {formatDate(devis.created_at)} par {devis.assigned_to_name}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {/* Desktop Edit Button */}
          <Link to={`/admin/devis/${id}/modifier`} className="hidden sm:block">
            <Button variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50">
              <Pencil className="w-4 h-4 mr-2" />
              Modifier
            </Button>
          </Link>
          
          {/* PDF Download */}
          <Button 
            variant="outline" 
            onClick={handleDownloadPdf} 
            disabled={pdfLoading}
            data-testid="download-pdf-btn"
          >
            {pdfLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Télécharger PDF
          </Button>
          
          {/* Odoo — Envoyer le devis */}
          {devis?.odoo_so_id ? (
            <a
              href={`https://erp.ma-grp.fr/odoo/sales/${devis.odoo_so_id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="bg-yellow-500 hover:bg-yellow-600 text-slate-900" title="Ouvrir dans Odoo pour envoyer le devis">
                <Mail className="w-4 h-4 mr-2" />
                Envoyer (Odoo)
              </Button>
            </a>
          ) : (
            <Button
              variant="outline"
              className="border-yellow-500 text-yellow-700"
              onClick={handleSyncToOdoo}
              disabled={syncingToOdoo}
              title="Créer ce devis dans Odoo pour pouvoir l'envoyer"
            >
              {syncingToOdoo ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
              {syncingToOdoo ? 'Sync Odoo…' : 'Créer dans Odoo'}
            </Button>
          )}
          {/* NB: Dialog email supprimé — envoi via Odoo uniquement */}
          
          {/* Relance Button - Only show if status allows */}
          {canRelance && (
            <Dialog open={relanceDialogOpen} onOpenChange={(open) => {
              if (open) {
                initializeRelanceDialog();
              } else {
                setRelanceDialogOpen(false);
              }
            }}>
              <DialogTrigger asChild>
                <Button 
                  variant="outline" 
                  className="border-orange-300 text-orange-700 hover:bg-orange-50"
                  data-testid="relance-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Relancer ({getRelanceLevel()}/3)
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: 'Barlow Condensed' }} className="flex items-center gap-2">
                    <RefreshCw className="w-5 h-5 text-orange-500" />
                    Relance {getRelanceLevel()} - Devis {devis?.reference}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                    <p className="text-sm text-orange-800">
                      {getRelanceLevel() < 4 ? (
                        <>
                          Cette action enverra un email de relance et passera le devis au statut 
                          <strong> "Relancé {getRelanceLevel()}"</strong>.
                        </>
                      ) : (
                        <>
                          <strong>Dernière relance :</strong> Cette action enverra un email de clôture et passera le devis au statut 
                          <strong> "Sans suite"</strong>.
                        </>
                      )}
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Adresse email du destinataire *</Label>
                    <Input
                      type="email"
                      value={relanceEmail}
                      onChange={(e) => setRelanceEmail(e.target.value)}
                      placeholder="client@exemple.com"
                      data-testid="relance-email-input"
                    />
                  </div>
                  
                  {/* CC Field */}
                  <div className="space-y-2">
                    <Label>Copie (CC)</Label>
                    <div className="flex gap-2">
                      <Input
                        type="email"
                        value={relanceCcInput}
                        onChange={(e) => setRelanceCcInput(e.target.value)}
                        placeholder="Ajouter un email en copie"
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addRelanceCc())}
                      />
                      <Button type="button" variant="outline" onClick={addRelanceCc}>+</Button>
                    </div>
                    {relanceCcList.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {relanceCcList.map((email, idx) => (
                          <span key={idx} className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 text-sm px-2 py-1 rounded">
                            {email}
                            <button type="button" onClick={() => removeRelanceCc(email)} className="hover:text-red-600">
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Message personnalisé (optionnel)</Label>
                    <Textarea
                      value={relanceMessage}
                      onChange={(e) => setRelanceMessage(e.target.value)}
                      placeholder="Laissez vide pour utiliser le message de relance par défaut"
                      rows={4}
                      data-testid="relance-message-input"
                    />
                    <p className="text-xs text-slate-500">
                      Si laissé vide, un message de relance adapté au niveau sera utilisé.
                    </p>
                  </div>
                  
                  <div className="flex justify-end gap-3">
                    <Button variant="outline" onClick={() => setRelanceDialogOpen(false)}>
                      Annuler
                    </Button>
                    <Button
                      onClick={handleSendRelance}
                      disabled={relanceLoading || !relanceEmail.trim()}
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                      data-testid="confirm-relance-btn"
                    >
                      {relanceLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      <RefreshCw className="w-4 h-4 mr-2" />
                      {getRelanceLevel() < 4 ? `Envoyer relance ${getRelanceLevel()}` : 'Clôturer le dossier'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
          
          {/* Assign Vendeur Button - Only for new devis */}
          {isAdmin() && devis.status === 'nouveau' && (
            <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-yellow-500 hover:bg-yellow-600" data-testid="assign-btn">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Assigner un vendeur
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Assigner ce devis à un vendeur</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <p className="text-sm text-slate-600">
                    Le vendeur sélectionné recevra un email de notification avec un lien vers ce devis.
                  </p>
                  <div className="space-y-2">
                    <Label>Sélectionner un vendeur</Label>
                    <Select value={assignVendeurId} onValueChange={setAssignVendeurId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choisir un vendeur" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.filter(u => u.role !== 'admin').map(u => (
                          <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
                      Annuler
                    </Button>
                    <Button 
                      onClick={handleAssignVendeur}
                      disabled={assignLoading || !assignVendeurId}
                      className="bg-yellow-500 hover:bg-yellow-600"
                    >
                      {assignLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Assigner et notifier
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
          
          {/* Status Actions */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="status-dropdown">
                Changer statut
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleStatusChange('nouveau')}>
                <Clock className="w-4 h-4 mr-2 text-yellow-500" />
                🆕 Nouveau
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleStatusChange('brouillon')}>
                <Clock className="w-4 h-4 mr-2" />
                Brouillon
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleStatusChange('envoye')}>
                <Send className="w-4 h-4 mr-2" />
                Envoyé
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleStatusChange('relance_1')}>
                <RefreshCw className="w-4 h-4 mr-2 text-orange-500" />
                Relancé 1
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleStatusChange('relance_2')}>
                <RefreshCw className="w-4 h-4 mr-2 text-orange-600" />
                Relancé 2
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleStatusChange('relance_3')}>
                <RefreshCw className="w-4 h-4 mr-2 text-orange-700" />
                Relancé 3
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleStatusChange('en_etude_financement')}>
                <FileText className="w-4 h-4 mr-2 text-purple-600" />
                📋 En étude financement
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleStatusChange('accepte')}>
                <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                Accepté
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleStatusChange('refuse')}>
                <XCircle className="w-4 h-4 mr-2 text-red-600" />
                Refusé
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleStatusChange('sans_suite')}>
                <XCircle className="w-4 h-4 mr-2 text-gray-600" />
                Sans suite
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleStatusChange('expire')}>
                <Clock className="w-4 h-4 mr-2 text-gray-400" />
                Expiré
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleStatusChange('archive')}>
                <Archive className="w-4 h-4 mr-2 text-purple-600" />
                Archivé
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Financing Link - show if there's a financing request */}
          {devis?.financing_request_id && (
            <Link to={`/admin/financement?request=${devis.financing_request_id}`}>
              <Button variant="outline" className="bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100">
                <Wallet className="w-4 h-4 mr-2" />
                Dossier financement
                {financingRequest && (
                  <Badge className="ml-2 bg-purple-200 text-purple-800 text-xs">
                    {financingRequest.documents?.filter(d => d.status === 'validated' || d.status === 'uploaded').length || 0}/
                    {financingRequest.documents?.length || 0}
                  </Badge>
                )}
              </Button>
            </Link>
          )}
          
          {/* Opportunity Link - show if there's a linked opportunity */}
          {devis?.opportunity_id && (
            <Link to={`/admin/opportunities/${devis.opportunity_id}`}>
              <Button variant="outline" className="bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100">
                <FolderOpen className="w-4 h-4 mr-2" />
                Voir l'opportunité
              </Button>
            </Link>
          )}
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="more-actions-btn">
                <MoreVertical className="w-5 h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {isAdmin() && (
                <>
                  <Dialog open={reassignDialogOpen} onOpenChange={setReassignDialogOpen}>
                    <DialogTrigger asChild>
                      <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                        <UserCog className="w-4 h-4 mr-2" />
                        Réassigner
                      </DropdownMenuItem>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Réassigner le devis</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Nouveau vendeur</Label>
                          <Select value={newUserId} onValueChange={setNewUserId}>
                            <SelectTrigger data-testid="reassign-user-select">
                              <SelectValue placeholder="Sélectionner un utilisateur" />
                            </SelectTrigger>
                            <SelectContent>
                              {users.map((u) => (
                                <SelectItem key={u.id} value={u.id}>
                                  {u.name} ({u.role})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex justify-end gap-3">
                          <Button variant="outline" onClick={() => setReassignDialogOpen(false)}>
                            Annuler
                          </Button>
                          <Button
                            onClick={handleReassign}
                            disabled={!newUserId || reassignLoading}
                            data-testid="confirm-reassign-btn"
                          >
                            {reassignLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Réassigner
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                  <DropdownMenuSeparator />
                </>
              )}
              <Link to={`/admin/devis/${id}/modifier`}>
                <DropdownMenuItem data-testid="edit-devis-btn">
                  <Pencil className="w-4 h-4 mr-2" />
                  Modifier
                </DropdownMenuItem>
              </Link>
              <DropdownMenuItem onClick={handleDuplicate} data-testid="duplicate-devis-btn">
                <Copy className="w-4 h-4 mr-2" />
                Dupliquer
              </DropdownMenuItem>
              {devis.status !== 'archive' && (
                <DropdownMenuItem onClick={handleArchive} data-testid="archive-devis-btn">
                  <Archive className="w-4 h-4 mr-2" />
                  Archiver
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleDelete}
                className="text-red-600"
                data-testid="delete-devis-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Supprimer
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - PDF Preview */}
        <div className="lg:col-span-2">
          {/* Hidden print content */}
          <div id="print-content" style={{ display: 'none' }}>
            <PrintableDevis devis={devis} snc={snc} apportClientPercent={apportClientPercent} />
          </div>
          
          {/* Preview Card */}
          <Card className="border-slate-200" ref={printRef}>
            <CardContent className="p-8">
              {/* PDF Header with IDMAT OI Branding */}
              <div className="flex justify-between items-start border-b-2 border-slate-800 pb-6 mb-6">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <img 
                      src={COMPANY_LOGO} 
                      alt={COMPANY_NAME}
                      className="h-12 object-contain"
                    />
                  </div>
                  <h2 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
                    DEVIS N° {devis.reference}
                  </h2>
                  <p className="text-slate-500 mt-2">
                    Date: {formatDate(devis.created_at)} | Validité: {devis.validity_days} jours
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-xs text-slate-500 space-y-0.5 mb-3">
                    <p>{COMPANY_ADDRESS}</p>
                    <p>{COMPANY_CITY}</p>
                    <p>SIRET: {COMPANY_SIRET}</p>
                    <p>{COMPANY_EMAIL}</p>
                    <p>{COMPANY_PHONE}</p>
                  </div>
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm text-slate-500 uppercase tracking-wide">Client</p>
                      {devis.client_id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                          onClick={handleOpenClientDialog}
                          title="Voir/Modifier le client"
                        >
                          <ExternalLink className="w-3 h-3 mr-1" />
                          Voir fiche
                        </Button>
                      )}
                    </div>
                    <p className="font-bold text-lg text-slate-800 flex items-center gap-2">
                      {devis.client_name}
                      {clientInfo && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-green-600 border-green-200 hover:bg-green-50 hover:border-green-400"
                          onClick={() => handleExportVCard()}
                          title="Exporter vers contacts (vCard)"
                          data-testid="export-vcard-btn"
                        >
                          <UserPlus className="w-4 h-4 mr-1" />
                          <span className="text-xs">vCard</span>
                        </Button>
                      )}
                    </p>
                    {clientInfo && (
                      <div className="mt-2 text-xs text-slate-600 space-y-1 text-left">
                        {clientInfo.phone && (
                          <a href={`tel:${clientInfo.phone}`} className="flex items-center gap-1.5 hover:text-blue-600">
                            <Phone className="w-3 h-3" />
                            {clientInfo.phone}
                          </a>
                        )}
                        {clientInfo.email && (
                          <a href={`mailto:${clientInfo.email}`} className="flex items-center gap-1.5 hover:text-blue-600">
                            <Mail className="w-3 h-3" />
                            {clientInfo.email}
                          </a>
                        )}
                        {(clientInfo.address || clientInfo.city) && (
                          <p className="flex items-center gap-1.5 text-slate-500">
                            <MapPin className="w-3 h-3" />
                            {[clientInfo.address, clientInfo.postal_code, clientInfo.city].filter(Boolean).join(', ')}
                          </p>
                        )}
                      </div>
                    )}
                    <Badge className={`mt-2 ${getStatusColor(devis.status)}`}>
                      {getStatusLabel(devis.status)}
                    </Badge>
                    {relanceResponse && (
                      <div className="mt-2 space-y-1">
                        <Badge className={relanceResponse.className}>
                          {relanceResponse.label}
                        </Badge>
                        {devis.last_client_response_at && (
                          <p className="text-xs text-slate-500">
                            Réponse reçue le {formatDateTime(devis.last_client_response_at)}
                          </p>
                        )}
                        {devis.client_relance_state === 'on_hold' && devis.relance_paused_until && (
                          <p className="text-xs text-slate-500">
                            Relance en pause jusqu&apos;au {formatDate(devis.relance_paused_until)}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {devis.devis_type === 'service' ? (
                <ServiceQuoteDetailSections devis={devis} />
              ) : (
                <>
                  {/* Machine - Large Image */}
                  <div className="mb-8">
                    <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                      <Package className="w-5 h-5 text-yellow-500" />
                      MACHINE SÉLECTIONNÉE
                    </h3>
                    <div className="bg-slate-50 rounded-lg p-4">
                      <div className="flex flex-col md:flex-row gap-6">
                        <div className="md:w-2/3">
                          {getImageUrl(devis.mini_pelle_image) ? (
                            <img
                              src={getImageUrl(devis.mini_pelle_image)}
                              alt={devis.mini_pelle_name}
                              className="w-full h-72 object-cover rounded-lg shadow-md"
                            />
                          ) : (
                            <div className="w-full h-72 bg-slate-200 rounded-lg flex items-center justify-center">
                              <Package className="w-20 h-20 text-slate-400" />
                            </div>
                          )}
                        </div>
                        <div className="md:w-1/3 flex flex-col justify-center">
                          <h4 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
                            {devis.mini_pelle_name}
                          </h4>
                          <p className="text-4xl font-mono font-bold text-yellow-500 mt-4">
                            {formatPrice(devis.mini_pelle_price)}
                          </p>
                          <p className="text-sm text-slate-500 mt-2">Prix de base HT</p>
                          {devis.mini_pelle_description && (
                            <p className="text-sm text-slate-600 mt-3 italic">{devis.mini_pelle_description}</p>
                          )}
                        </div>
                      </div>

                      {devis.mini_pelle_specs && Object.values(devis.mini_pelle_specs).some(v => v) && (
                        <div className="mt-4 pt-4 border-t border-slate-200">
                          <h5 className="text-sm font-bold text-slate-700 mb-3 uppercase tracking-wide">
                            Caractéristiques techniques
                          </h5>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                            {devis.mini_pelle_specs.longueur && (
                              <div>
                                <span className="text-slate-500">Longueur:</span>
                                <span className="ml-1 font-medium text-slate-800">{devis.mini_pelle_specs.longueur}</span>
                              </div>
                            )}
                            {devis.mini_pelle_specs.largeur && (
                              <div>
                                <span className="text-slate-500">Largeur:</span>
                                <span className="ml-1 font-medium text-slate-800">{devis.mini_pelle_specs.largeur}</span>
                              </div>
                            )}
                            {devis.mini_pelle_specs.profondeur_fouille && (
                              <div>
                                <span className="text-slate-500">Prof. fouille:</span>
                                <span className="ml-1 font-medium text-slate-800">{devis.mini_pelle_specs.profondeur_fouille}</span>
                              </div>
                            )}
                            {devis.mini_pelle_specs.hauteur_chargement && (
                              <div>
                                <span className="text-slate-500">Haut. chargement:</span>
                                <span className="ml-1 font-medium text-slate-800">{devis.mini_pelle_specs.hauteur_chargement}</span>
                              </div>
                            )}
                            {devis.mini_pelle_specs.weight && (
                              <div>
                                <span className="text-slate-500">Poids:</span>
                                <span className="ml-1 font-medium text-slate-800">{devis.mini_pelle_specs.weight}</span>
                              </div>
                            )}
                            {devis.mini_pelle_specs.marque_moteur && (
                              <div>
                                <span className="text-slate-500">Moteur:</span>
                                <span className="ml-1 font-medium text-slate-800">{devis.mini_pelle_specs.marque_moteur}</span>
                              </div>
                            )}
                            {devis.mini_pelle_specs.nombre_cylindres && (
                              <div>
                                <span className="text-slate-500">Cylindres:</span>
                                <span className="ml-1 font-medium text-slate-800">{devis.mini_pelle_specs.nombre_cylindres}</span>
                              </div>
                            )}
                            {devis.mini_pelle_specs.puissance_moteur && (
                              <div>
                                <span className="text-slate-500">Puissance:</span>
                                <span className="ml-1 font-medium text-slate-800">{devis.mini_pelle_specs.puissance_moteur}</span>
                              </div>
                            )}
                            {devis.mini_pelle_specs.marque_pompe && (
                              <div>
                                <span className="text-slate-500">Pompe:</span>
                                <span className="ml-1 font-medium text-slate-800">{devis.mini_pelle_specs.marque_pompe}</span>
                              </div>
                            )}
                            {devis.mini_pelle_specs.debit_pompe && (
                              <div>
                                <span className="text-slate-500">Débit:</span>
                                <span className="ml-1 font-medium text-slate-800">{devis.mini_pelle_specs.debit_pompe}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {devis.options && devis.options.length > 0 && (
                    <div className="mb-8">
                      <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                        <Wrench className="w-5 h-5 text-yellow-500" />
                        OPTIONS ET ACCESSOIRES ({devis.options.length})
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {devis.options.map((opt, index) => (
                          <div key={index} className="bg-slate-50 rounded-lg p-3 flex items-center gap-3">
                            {getImageUrl(opt.image_url) ? (
                              <img
                                src={getImageUrl(opt.image_url)}
                                alt={opt.option_name}
                                className="w-14 h-14 object-cover rounded-md flex-shrink-0"
                              />
                            ) : (
                              <div className="w-14 h-14 bg-slate-200 rounded-md flex items-center justify-center flex-shrink-0">
                                <Wrench className="w-6 h-6 text-slate-400" />
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-slate-800 truncate">{opt.option_name}</p>
                              <p className={cn(
                                "text-sm font-mono font-semibold",
                                opt.price === 0 || opt.included ? "text-green-600" : "text-yellow-600"
                              )}>
                                {opt.price === 0 || opt.included ? '✓ Inclus' : formatPrice(opt.price)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {devis.notes && (
                    <div className="mb-8 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <h3 className="text-sm font-bold text-amber-800 mb-2 uppercase tracking-wide">Notes</h3>
                      <p className="text-amber-900 whitespace-pre-wrap">{devis.notes}</p>
                    </div>
                  )}

                  <div className="border-t-2 border-slate-200 pt-6">
                    <div className="max-w-md ml-auto">
                      <div className="space-y-3">
                        <div className="flex justify-between text-slate-600">
                          <span>Machine</span>
                          <span className="font-mono">{formatPrice(devis.mini_pelle_price)}</span>
                        </div>
                        <div className="flex justify-between text-slate-600">
                          <span>Options ({devis.options?.length || 0})</span>
                          <span className="font-mono">{formatPrice(devis.total_options)}</span>
                        </div>
                        <Separator />
                        <div className="flex justify-between">
                          <span className="text-slate-600">Sous-total HT</span>
                          <span className="font-mono font-semibold">{formatPrice(devis.subtotal)}</span>
                        </div>
                        {devis.discount_amount > 0 && (
                          <div className="flex justify-between text-green-600">
                            <span>Remise ({devis.discount}{devis.discount_type === 'percent' ? '%' : '€'})</span>
                            <span className="font-mono">-{formatPrice(devis.discount_amount)}</span>
                          </div>
                        )}
                        <div className="flex justify-between bg-slate-800 text-white p-4 rounded-lg -mx-2">
                          <span className="text-xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
                            TOTAL TTC
                          </span>
                          <span className="text-2xl font-mono font-bold">
                            {formatPrice(devis.total)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Simulation SNC - Conditional */}
              {devis.devis_type !== 'service' && devis.show_snc !== false && (
                <div className="mt-8 border-2 border-yellow-200 rounded-lg overflow-hidden">
                  <div className="bg-yellow-500 text-slate-900 px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Calculator className="w-5 h-5" />
                      <h3 className="font-bold text-lg" style={{ fontFamily: 'Barlow Condensed' }}>
                        SIMULATION FINANCEMENT SNC
                      </h3>
                    </div>
                  </div>
                  <div className="p-4 bg-yellow-50">
                    {/* Apport client modifiable */}
                    <div className="mb-4 flex items-center gap-3 bg-white p-3 rounded-lg border border-yellow-200">
                      <label className="text-sm font-medium text-slate-700 whitespace-nowrap">
                        Apport client :
                      </label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="0"
                          max="75"
                          value={apportClientPercent}
                          onChange={(e) => setApportClientPercent(Math.max(0, Math.min(75, Number(e.target.value) || 0)))}
                          className="w-20 text-center font-mono"
                          data-testid="apport-client-input"
                        />
                        <span className="text-slate-600 font-medium">%</span>
                        <span className="text-slate-500 text-sm ml-2">
                          = {formatPrice(snc.apportClient)}
                        </span>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="bg-white p-4 rounded-lg border border-yellow-200">
                        <p className="text-sm text-slate-500 uppercase tracking-wide mb-1">Apport SNC (30%)</p>
                        <p className="text-xl font-mono font-bold text-slate-800">{formatPrice(snc.apportSNC)}</p>
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-yellow-200">
                        <p className="text-sm text-slate-500 uppercase tracking-wide mb-1">Apport client ({apportClientPercent}%)</p>
                        <p className="text-xl font-mono font-bold text-slate-800">{formatPrice(snc.apportClient)}</p>
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-yellow-200">
                        <p className="text-sm text-slate-500 uppercase tracking-wide mb-1">Montant financé</p>
                        <p className="text-xl font-mono font-bold text-slate-800">{formatPrice(snc.totalFinance)}</p>
                      </div>
                      <div className="bg-yellow-500 text-slate-900 p-4 rounded-lg">
                        <p className="text-sm text-yellow-100 uppercase tracking-wide mb-1">Loyer mensuel (60 mois)</p>
                        <p className="text-xl font-mono font-bold">{formatPrice(snc.loyerMensuel)}/mois</p>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-3 italic">
                      * Simulation indicative basée sur un financement SNC sur 60 mois avec coefficient 1.2. Apport total: {formatPrice(snc.totalApport)} ({25 + apportClientPercent}%). Sous réserve d'acceptation du dossier.
                    </p>
                    
                    {/* Cabinet SNC & Banques sélectionnés */}
                    <div className="mt-4 pt-4 border-t border-yellow-200 space-y-3">
                      {devis?.cabinet_snc_name && (
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-yellow-600" />
                          <span className="text-sm font-medium text-slate-700">Cabinet SNC :</span>
                          <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300">
                            {devis.cabinet_snc_name}
                          </Badge>
                        </div>
                      )}
                      {devis?.banques && devis.banques.length > 0 && (
                        <div className="flex items-start gap-2">
                          <Banknote className="w-4 h-4 text-yellow-600 mt-0.5" />
                          <span className="text-sm font-medium text-slate-700">Banque(s) :</span>
                          <div className="flex flex-wrap gap-1">
                            {devis.banques.map((banque, idx) => (
                              <Badge key={idx} className="bg-blue-100 text-blue-800 border-blue-300">
                                {banque.name}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {(devis?.cabinet_snc_name || (devis?.banques && devis.banques.length > 0)) && (
                        <p className="text-xs text-slate-500">
                          Ces organismes recevront automatiquement le devis en copie lors de l'envoi par email.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Financing Request Section */}
              {devis.devis_type !== 'service' && devis.show_snc !== false && (
                <div className="mt-8 border-2 border-purple-200 rounded-lg overflow-hidden" data-testid="financing-section">
                  <div className="bg-purple-600 text-white px-4 py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Banknote className="w-5 h-5" />
                        <h3 className="font-bold text-lg" style={{ fontFamily: 'Barlow Condensed' }}>
                          DEMANDE DE FINANCEMENT
                        </h3>
                      </div>
                      {financingRequest && (
                        <Badge className={getFinancingStatusInfo(financingRequest.status).color}>
                          {getFinancingStatusInfo(financingRequest.status).name}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="p-4 bg-purple-50">
                    {!financingRequest ? (
                      // No financing request yet
                      <div className="text-center py-6">
                        <FolderOpen className="w-12 h-12 text-purple-300 mx-auto mb-3" />
                        <p className="text-slate-600 mb-4">
                          Aucune demande de financement n'a été créée pour ce devis.
                        </p>
                        <Button
                          onClick={handleCreateFinancingRequest}
                          disabled={financingLoading}
                          className="bg-purple-600 hover:bg-purple-700 text-white"
                          data-testid="create-financing-btn"
                        >
                          {financingLoading ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Plus className="w-4 h-4 mr-2" />
                          )}
                          Créer une demande de financement
                        </Button>
                      </div>
                    ) : (
                      // Financing request exists - show documents
                      <div className="space-y-4">
                        {/* Progress bar */}
                        <div className="bg-white p-4 rounded-lg border border-purple-200">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-slate-700">Progression du dossier</span>
                            <span className="text-sm font-bold text-purple-600">{financingRequest.progress || 0}%</span>
                          </div>
                          <div className="w-full bg-purple-100 rounded-full h-2">
                            <div 
                              className="bg-purple-600 h-2 rounded-full transition-all duration-300" 
                              style={{ width: `${financingRequest.progress || 0}%` }}
                            />
                          </div>
                        </div>

                        {/* Status selector (admin only) */}
                        {isAdmin() && (
                          <div className="flex items-center gap-3 bg-white p-3 rounded-lg border border-purple-200">
                            <label className="text-sm font-medium text-slate-700 whitespace-nowrap">
                              Statut :
                            </label>
                            <Select 
                              value={financingRequest.status} 
                              onValueChange={handleUpdateFinancingStatus}
                            >
                              <SelectTrigger className="w-48">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="en_cours">En cours</SelectItem>
                                <SelectItem value="dossier_complet">Dossier complet</SelectItem>
                                <SelectItem value="envoye_banque">Envoyé banque</SelectItem>
                                <SelectItem value="accepte">Accepté</SelectItem>
                                <SelectItem value="refuse">Refusé</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}

                        {/* Documents list */}
                        <div>
                          <h4 className="text-sm font-bold text-slate-700 mb-3 uppercase tracking-wide flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            Pièces justificatives
                          </h4>
                          <div className="space-y-2">
                            {financingRequest.documents?.map((doc) => (
                              <div
                                key={doc.doc_id}
                                className={cn(
                                  'flex items-center justify-between p-3 rounded-lg border bg-white',
                                  getDocStatusColor(doc.status)
                                )}
                              >
                                <div className="flex items-center gap-3">
                                  <div className={cn(
                                    'w-8 h-8 rounded-full flex items-center justify-center',
                                    doc.status === 'validated' ? 'bg-green-100 text-green-600' :
                                    doc.status === 'rejected' ? 'bg-red-100 text-red-600' :
                                    doc.status === 'uploaded' ? 'bg-blue-100 text-blue-600' :
                                    'bg-slate-100 text-slate-400'
                                  )}>
                                    {doc.status === 'validated' ? <Check className="w-4 h-4" /> :
                                     doc.status === 'rejected' ? <X className="w-4 h-4" /> :
                                     doc.status === 'uploaded' ? <FileText className="w-4 h-4" /> :
                                     <Clock className="w-4 h-4" />}
                                  </div>
                                  <div>
                                    <p className="font-medium text-slate-800">
                                      {doc.name}
                                      {doc.required && <span className="text-red-500 ml-1">*</span>}
                                    </p>
                                    <p className="text-xs text-slate-500">
                                      {getDocStatusLabel(doc.status)}
                                      {doc.file_name && ` - ${doc.file_name}`}
                                      {doc.rejection_reason && (
                                        <span className="text-red-500"> ({doc.rejection_reason})</span>
                                      )}
                                    </p>
                                  </div>
                                </div>

                                <div className="flex items-center gap-1">
                                  {(doc.status === 'pending' || doc.status === 'rejected') && (
                                    <>
                                      <input
                                        type="file"
                                        id={`financing-file-${doc.doc_id}`}
                                        className="hidden"
                                        onChange={(e) => {
                                          if (e.target.files?.[0]) {
                                            handleUploadFinancingDoc(doc.doc_id, e.target.files[0]);
                                          }
                                        }}
                                      />
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => document.getElementById(`financing-file-${doc.doc_id}`)?.click()}
                                        disabled={uploadingDocId === doc.doc_id}
                                        data-testid={`upload-doc-${doc.doc_id}`}
                                      >
                                        {uploadingDocId === doc.doc_id ? (
                                          <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                          <Upload className="w-4 h-4" />
                                        )}
                                      </Button>
                                    </>
                                  )}
                                  
                                  {doc.status === 'uploaded' && isAdmin() && (
                                    <>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                        onClick={() => handleValidateFinancingDoc(doc.doc_id)}
                                        title="Valider"
                                      >
                                        <Check className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                        onClick={() => handleRejectFinancingDoc(doc.doc_id)}
                                        title="Rejeter"
                                      >
                                        <X className="w-4 h-4" />
                                      </Button>
                                    </>
                                  )}

                                  {doc.file_path && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={async () => {
                                        try {
                                          const response = await financingAPI.downloadDocument(financingRequest.id, doc.doc_id);
                                          const url = window.URL.createObjectURL(new Blob([response.data]));
                                          const link = document.createElement('a');
                                          link.href = url;
                                          link.download = doc.file_name || `${doc.doc_id}.pdf`;
                                          link.click();
                                          window.URL.revokeObjectURL(url);
                                        } catch (e) {
                                          toast.error('Erreur lors du téléchargement');
                                        }
                                      }}
                                      title="Télécharger"
                                    >
                                      <Download className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            ))}

                            {(!financingRequest.documents || financingRequest.documents.length === 0) && (
                              <p className="text-center text-slate-500 py-4">
                                Aucune pièce requise configurée pour ce type de client.
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Link to full financing page */}
                        <div className="text-center pt-2">
                          <Link to="/admin/financement">
                            <Button variant="outline" size="sm">
                              <FolderOpen className="w-4 h-4 mr-2" />
                              Voir toutes les demandes de financement
                            </Button>
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - Activities */}
        <div className="space-y-6 no-print">
          {/* Add Activity */}
          <Card className="border-slate-200">
            <CardHeader className="border-b border-slate-100 bg-slate-50/50 flex flex-row items-center justify-between">
              <CardTitle style={{ fontFamily: 'Barlow Condensed' }}>Suivi</CardTitle>
              <div className="flex items-center gap-2">
                {/* Create Task Button */}
                <Dialog open={taskDialogOpen} onOpenChange={(open) => {
                  setTaskDialogOpen(open);
                  if (!open) {
                    setEditingTask(null);
                    setTaskTitle('');
                    setTaskDescription('');
                    setTaskType('rappel');
                    setTaskPriority('normale');
                    setTaskDueDate('');
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline" data-testid="add-task-btn">
                      <CheckSquare className="w-4 h-4 mr-1" />
                      Tâche
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
                        {editingTask ? 'Modifier la tâche' : 'Créer une tâche'}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Titre *</Label>
                        <Input
                          value={taskTitle}
                          onChange={(e) => setTaskTitle(e.target.value)}
                          placeholder="Ex: Relancer le client, Préparer les documents..."
                          data-testid="task-title-input"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Type</Label>
                          <Select value={taskType} onValueChange={setTaskType}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="rappel">Rappel</SelectItem>
                              <SelectItem value="relance">Relance</SelectItem>
                              <SelectItem value="appel">Appel</SelectItem>
                              <SelectItem value="email">Email</SelectItem>
                              <SelectItem value="rdv">RDV</SelectItem>
                              <SelectItem value="autre">Autre</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Priorité</Label>
                          <Select value={taskPriority} onValueChange={setTaskPriority}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="basse">Basse</SelectItem>
                              <SelectItem value="normale">Normale</SelectItem>
                              <SelectItem value="haute">Haute</SelectItem>
                              <SelectItem value="urgente">Urgente</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Date d'échéance *</Label>
                        <Input
                          type="date"
                          value={taskDueDate}
                          onChange={(e) => setTaskDueDate(e.target.value)}
                          data-testid="task-due-date-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Description</Label>
                        <Textarea
                          value={taskDescription}
                          onChange={(e) => setTaskDescription(e.target.value)}
                          rows={2}
                          placeholder="Détails supplémentaires..."
                        />
                      </div>
                      <div className="flex justify-end gap-3">
                        <Button variant="outline" onClick={() => setTaskDialogOpen(false)}>
                          Annuler
                        </Button>
                        <Button
                          onClick={handleCreateTask}
                          disabled={!taskTitle.trim() || !taskDueDate || taskLoading}
                          className="bg-slate-800 hover:bg-slate-900"
                          data-testid="save-task-btn"
                        >
                          {taskLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                          Créer la tâche
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
                
                {/* Add Activity Button */}
                <Dialog open={activityDialogOpen} onOpenChange={setActivityDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="bg-yellow-500 hover:bg-yellow-600 text-slate-900" data-testid="add-activity-btn">
                      Ajouter
                    </Button>
                  </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
                      Nouvelle activité
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Type</Label>
                      <div className="grid grid-cols-4 gap-2">
                        {[
                          { type: 'appel', icon: Phone, label: 'Appel' },
                          { type: 'sms', icon: MessageSquare, label: 'SMS' },
                          { type: 'mail', icon: Mail, label: 'Email' },
                          { type: 'note', icon: StickyNote, label: 'Note' },
                        ].map(({ type, icon: Icon, label }) => (
                          <button
                            key={type}
                            type="button"
                            onClick={() => setActivityType(type)}
                            className={cn(
                              'flex flex-col items-center gap-1 p-3 rounded-md border transition-colors',
                              activityType === type
                                ? 'border-yellow-500 bg-yellow-50 text-yellow-600'
                                : 'border-slate-200 hover:border-slate-300'
                            )}
                            data-testid={`activity-type-${type}`}
                          >
                            <Icon className="w-5 h-5" />
                            <span className="text-xs">{label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Contenu</Label>
                      <Textarea
                        value={activityContent}
                        onChange={(e) => setActivityContent(e.target.value)}
                        rows={4}
                        placeholder="Décrivez l'activité..."
                        data-testid="activity-content-input"
                      />
                    </div>
                    <div className="flex justify-end gap-3">
                      <Button variant="outline" onClick={() => setActivityDialogOpen(false)}>
                        Annuler
                      </Button>
                      <Button
                        onClick={handleAddActivity}
                        disabled={!activityContent.trim() || activityLoading}
                        className="bg-slate-800 hover:bg-slate-900"
                        data-testid="save-activity-btn"
                      >
                        {activityLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        Enregistrer
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
              </div>
            </CardHeader>
            
            {/* Tasks Section */}
            {devisTasks.length > 0 && (
              <div className="border-b border-slate-100">
                <div className="px-4 py-2 bg-slate-50 text-xs font-medium text-slate-600 uppercase tracking-wider">
                  Tâches ({devisTasks.length})
                </div>
                <div className="divide-y divide-slate-100">
                  {devisTasks.map((task) => (
                    <div key={task.id} className={`p-3 flex items-center gap-3 ${task.status === 'termine' ? 'bg-green-50/50' : ''}`}>
                      <button 
                        onClick={() => task.status !== 'termine' && handleCompleteTask(task.id)}
                        className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                          task.status === 'termine' 
                            ? 'bg-green-500 border-green-500 text-white' 
                            : 'border-slate-300 hover:border-green-500 hover:bg-green-50'
                        }`}
                        title={task.status === 'termine' ? 'Terminée' : 'Marquer comme fait'}
                      >
                        {task.status === 'termine' && <Check className="w-4 h-4" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-medium ${task.status === 'termine' ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                            {task.title}
                          </span>
                          <Badge variant="outline" className={`text-xs ${
                            task.priority === 'urgente' ? 'border-red-300 text-red-600' :
                            task.priority === 'haute' ? 'border-orange-300 text-orange-600' :
                            'border-slate-300 text-slate-600'
                          }`}>
                            {task.priority}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                          <Calendar className="w-3 h-3" />
                          {task.due_date ? new Date(task.due_date).toLocaleDateString('fr-FR') : '-'}
                          {task.status === 'termine' && (
                            <span className="text-green-600 font-medium">✓ Terminée</span>
                          )}
                        </div>
                      </div>
                      {task.status !== 'termine' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2"
                          onClick={() => handleEditTask(task)}
                        >
                          <Pencil className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <CardContent className="p-0">
              {(!devis.activities || devis.activities.length === 0) ? (
                <div className="p-6 text-center text-slate-500">
                  Aucune activité enregistrée
                </div>
              ) : (
                <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
                  {[...devis.activities].reverse().map((activity, index) => {
                    const IconComponent = getActivityIconComponent(activity.type);
                    const iconStyle = getActivityIcon(activity.type);
                    
                    return (
                      <div key={activity.id || index} className="p-4" data-testid={`activity-${index}`}>
                        <div className="flex gap-3">
                          <div className={cn(
                            'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
                            iconStyle.color
                          )}>
                            <IconComponent className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-medium text-slate-800 capitalize">
                                {activity.type}
                              </span>
                              <span className="text-xs text-slate-400">
                                par {activity.created_by_name}
                              </span>
                            </div>
                            <p className="text-sm text-slate-600 whitespace-pre-wrap">
                              {activity.content}
                            </p>
                            <p className="text-xs text-slate-400 mt-1">
                              {formatDateTime(activity.created_at)}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* AI Task Suggestions Banner */}
          {noteSuggestions.length > 0 && (
            <Card className="border-yellow-200 bg-yellow-50/50" data-testid="ai-suggestions-banner">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-yellow-500" />
                  Suggestion de tâche
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {noteSuggestions.map((suggestion, idx) => (
                  <div key={idx} className="flex items-start justify-between gap-3 p-3 bg-white rounded-lg border border-yellow-200">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={
                          suggestion.priority === 'urgente' ? 'bg-red-100 text-red-700' :
                          suggestion.priority === 'haute' ? 'bg-orange-100 text-orange-700' :
                          'bg-blue-100 text-blue-700'
                        }>
                          {suggestion.priority}
                        </Badge>
                        <span className="font-medium text-slate-800">{suggestion.title}</span>
                      </div>
                      <p className="text-xs text-slate-500 mb-1">{suggestion.reason}</p>
                      <p className="text-xs text-slate-600">
                        📅 Date suggérée: <strong>{suggestion.suggested_date}</strong> à <strong>{suggestion.suggested_time}</strong>
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        className="bg-yellow-500 hover:bg-yellow-600 text-slate-900"
                        onClick={() => handleCreateTaskFromSuggestion(suggestion)}
                        data-testid={`create-task-suggestion-${idx}`}
                      >
                        <Zap className="w-3 h-3 mr-1" />
                        Créer
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => dismissSuggestion(suggestion)}
                        className="text-slate-400 hover:text-slate-600"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Quick Info */}
          <Card className="border-slate-200">
            <CardContent className="p-4 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Statut</span>
                <Badge className={getStatusColor(devis.status)}>
                  {getStatusLabel(devis.status)}
                </Badge>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Vendeur</span>
                <span className="text-slate-800">{devis.assigned_to_name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Créé le</span>
                <span className="text-slate-800">{formatDate(devis.created_at)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Mis à jour</span>
                <span className="text-slate-800">{formatDate(devis.updated_at)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Internal Chat */}
          <EntityChat
            entityType="devis"
            entityId={devis.id}
            entityName={devis.reference}
          />
        </div>
      </div>

      {/* Client Dialog */}
      <Dialog open={clientDialogOpen} onOpenChange={(open) => {
        setClientDialogOpen(open);
        if (!open) setClientEditMode(false);
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Fiche Client</span>
              {!clientEditMode && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setClientEditMode(true)}
                >
                  <Pencil className="w-4 h-4 mr-1" />
                  Modifier
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {clientInfo && (
            <div className="space-y-4 py-4">
              {clientEditMode ? (
                // Edit mode
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="text-sm font-medium text-slate-700">Nom *</label>
                      <Input
                        value={clientForm.name}
                        onChange={(e) => setClientForm({...clientForm, name: e.target.value})}
                        placeholder="Nom du client"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700">Email</label>
                      <Input
                        type="email"
                        value={clientForm.email}
                        onChange={(e) => setClientForm({...clientForm, email: e.target.value})}
                        placeholder="Email"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700">Téléphone</label>
                      <Input
                        value={clientForm.phone}
                        onChange={(e) => setClientForm({...clientForm, phone: e.target.value})}
                        placeholder="Téléphone"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="text-sm font-medium text-slate-700">Société</label>
                      <Input
                        value={clientForm.company}
                        onChange={(e) => setClientForm({...clientForm, company: e.target.value})}
                        placeholder="Nom de la société"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="text-sm font-medium text-slate-700">Adresse</label>
                      <Input
                        value={clientForm.address}
                        onChange={(e) => setClientForm({...clientForm, address: e.target.value})}
                        placeholder="Adresse"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700">Code postal</label>
                      <Input
                        value={clientForm.postal_code}
                        onChange={(e) => setClientForm({...clientForm, postal_code: e.target.value})}
                        placeholder="Code postal"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700">Ville</label>
                      <Input
                        value={clientForm.city}
                        onChange={(e) => setClientForm({...clientForm, city: e.target.value})}
                        placeholder="Ville"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="text-sm font-medium text-slate-700">
                        Catégorie (pour financement SNC) 
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <Select
                        value={clientForm.category}
                        onValueChange={(value) => setClientForm({...clientForm, category: value})}
                      >
                        <SelectTrigger className={!clientForm.category ? 'border-red-300' : ''}>
                          <SelectValue placeholder="Sélectionner une catégorie" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="agriculteur">Agriculteur</SelectItem>
                          <SelectItem value="professionnel">Professionnel</SelectItem>
                          <SelectItem value="particulier">Particulier</SelectItem>
                          <SelectItem value="collectivite">Collectivité</SelectItem>
                        </SelectContent>
                      </Select>
                      {!clientForm.category && (
                        <p className="text-xs text-red-500 mt-1">Catégorie obligatoire</p>
                      )}
                    </div>
                    <div className="col-span-2">
                      <label className="text-sm font-medium text-slate-700">Notes</label>
                      <Textarea
                        value={clientForm.notes}
                        onChange={(e) => setClientForm({...clientForm, notes: e.target.value})}
                        placeholder="Notes..."
                        rows={3}
                      />
                    </div>
                  </div>
                  
                  <div className="flex justify-end gap-2 pt-4 border-t">
                    <Button variant="outline" onClick={() => setClientEditMode(false)}>
                      Annuler
                    </Button>
                    <Button 
                      onClick={handleSaveClient} 
                      disabled={savingClient || !clientForm.category} 
                      className="bg-yellow-500 hover:bg-yellow-600"
                    >
                      {savingClient ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Enregistrer
                    </Button>
                  </div>
                </div>
              ) : (
                // View mode
                <div className="space-y-4">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h3 className="font-semibold text-lg text-slate-800">{clientInfo.name}</h3>
                    {clientInfo.company && (
                      <p className="text-sm text-slate-600">{clientInfo.company}</p>
                    )}
                    <div className="flex gap-2 mt-2">
                      <Badge variant="outline">
                        {clientInfo.contact_type || 'prospect'}
                      </Badge>
                      {clientInfo.category && (
                        <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300">
                          {clientInfo.category === 'agriculteur' ? 'Agriculteur' :
                           clientInfo.category === 'professionnel' ? 'Professionnel' :
                           clientInfo.category === 'particulier' ? 'Particulier' :
                           clientInfo.category === 'collectivite' ? 'Collectivité' :
                           clientInfo.category}
                        </Badge>
                      )}
                      {!clientInfo.category && (
                        <Badge className="bg-red-100 text-red-800 border-red-300">
                          ⚠️ Catégorie manquante
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {clientInfo.email && (
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="w-4 h-4 text-slate-400" />
                        <a href={`mailto:${clientInfo.email}`} className="text-blue-600 hover:underline">
                          {clientInfo.email}
                        </a>
                      </div>
                    )}
                    {clientInfo.phone && (
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="w-4 h-4 text-slate-400" />
                        <a href={`tel:${clientInfo.phone}`} className="text-blue-600 hover:underline">
                          {clientInfo.phone}
                        </a>
                      </div>
                    )}
                  </div>
                  
                  {(clientInfo.address || clientInfo.city) && (
                    <div className="flex items-start gap-2 text-sm text-slate-600">
                      <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                      <span>
                        {[clientInfo.address, clientInfo.postal_code, clientInfo.city].filter(Boolean).join(', ')}
                      </span>
                    </div>
                  )}
                  
                  {clientInfo.notes && (
                    <div className="bg-yellow-50 rounded-lg p-3 text-sm text-slate-700">
                      <p className="font-medium text-yellow-800 mb-1">Notes</p>
                      {clientInfo.notes}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Composant pour l'impression PDF
const PrintableDevis = ({ devis, snc, apportClientPercent = 10 }) => {
  return (
    <div style={{ 
      fontFamily: 'Arial, sans-serif', 
      padding: '40px', 
      maxWidth: '800px', 
      margin: '0 auto',
      backgroundColor: 'white'
    }}>
      {/* En-tête */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        borderBottom: '3px solid #1e293b',
        paddingBottom: '20px',
        marginBottom: '30px'
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
            <div style={{ 
              width: '40px', 
              height: '40px', 
              backgroundColor: '#f97316', 
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '20px'
            }}>
              MP
            </div>
            <span style={{ fontSize: '24px', fontWeight: 'bold', color: '#1e293b' }}>
              MINI PELLE CRM
            </span>
          </div>
          <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1e293b', margin: '0' }}>
            DEVIS N° {devis.reference}
          </h1>
          <p style={{ color: '#64748b', marginTop: '8px' }}>
            Date: {formatDate(devis.created_at)} | Validité: {devis.validity_days} jours
          </p>
        </div>
        <div style={{ 
          textAlign: 'right', 
          backgroundColor: '#f8fafc', 
          padding: '15px 20px',
          borderRadius: '8px'
        }}>
          <p style={{ fontSize: '12px', color: '#64748b', textTransform: 'uppercase', marginBottom: '5px' }}>
            Client
          </p>
          <p style={{ fontSize: '18px', fontWeight: 'bold', color: '#1e293b' }}>
            {devis.client_name}
          </p>
        </div>
      </div>

      {devis.devis_type === 'service' ? (
        <>
          <div style={{ marginBottom: '30px' }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: 'bold',
              color: '#1e293b',
              borderBottom: '2px solid #f97316',
              paddingBottom: '8px',
              marginBottom: '15px'
            }}>
              PRESTATIONS ET FOURNITURES
            </h2>
            <div style={{ display: 'grid', gap: '12px' }}>
              {(devis.service_lines || []).map((line, index) => (
                <div key={line.id || index} style={{ backgroundColor: '#f8fafc', padding: '14px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                    <div>
                      <p style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b', margin: '0 0 4px 0' }}>
                        {line.designation}
                      </p>
                      <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 6px 0' }}>
                        {(line.category || 'prestation').replaceAll('_', ' ')}
                      </p>
                      {line.description && (
                        <p style={{ fontSize: '12px', color: '#475569', margin: 0 }}>{line.description}</p>
                      )}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 4px 0' }}>
                        {line.quantity} {line.unit} x {formatPrice(line.unit_price)}
                      </p>
                      <p style={{ fontSize: '18px', fontWeight: '700', fontFamily: 'monospace', color: '#1e293b', margin: 0 }}>
                        {formatPrice(line.line_total || ((line.quantity || 0) * (line.unit_price || 0)))}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {devis.notes && (
            <div style={{
              marginBottom: '30px',
              backgroundColor: '#fef3c7',
              border: '1px solid #fcd34d',
              padding: '15px',
              borderRadius: '8px'
            }}>
              <p style={{ fontSize: '12px', fontWeight: 'bold', color: '#92400e', marginBottom: '5px' }}>
                NOTES
              </p>
              <p style={{ color: '#78350f', margin: '0' }}>{devis.notes}</p>
            </div>
          )}

          <div style={{
            borderTop: '2px solid #e2e8f0',
            paddingTop: '20px',
            marginBottom: '30px'
          }}>
            <div style={{ maxWidth: '350px', marginLeft: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', color: '#64748b' }}>
                <span>Prestations / pièces</span>
                <span style={{ fontFamily: 'monospace' }}>{formatPrice(devis.service_lines_total || devis.subtotal)}</span>
              </div>
              <div style={{ borderTop: '1px solid #e2e8f0', marginTop: '8px', paddingTop: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                  <span style={{ color: '#64748b' }}>Sous-total HT</span>
                  <span style={{ fontFamily: 'monospace', fontWeight: '600' }}>{formatPrice(devis.subtotal)}</span>
                </div>
              </div>
              {devis.discount_amount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', color: '#16a34a' }}>
                  <span>Remise</span>
                  <span style={{ fontFamily: 'monospace' }}>-{formatPrice(devis.discount_amount)}</span>
                </div>
              )}
              <div style={{
                backgroundColor: '#1e293b',
                color: 'white',
                padding: '15px',
                borderRadius: '8px',
                marginTop: '10px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span style={{ fontSize: '18px', fontWeight: 'bold' }}>TOTAL TTC</span>
                <span style={{ fontSize: '24px', fontWeight: 'bold', fontFamily: 'monospace' }}>
                  {formatPrice(devis.total)}
                </span>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div style={{ marginBottom: '30px' }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: 'bold',
              color: '#1e293b',
              borderBottom: '2px solid #f97316',
              paddingBottom: '8px',
              marginBottom: '15px'
            }}>
              MACHINE SÉLECTIONNÉE
            </h2>
            <div style={{
              display: 'flex',
              gap: '20px',
              backgroundColor: '#f8fafc',
              padding: '20px',
              borderRadius: '8px'
            }}>
              {getImageUrl(devis.mini_pelle_image) && (
                <img
                  src={getImageUrl(devis.mini_pelle_image)}
                  alt={devis.mini_pelle_name}
                  style={{
                    width: '300px',
                    height: '200px',
                    objectFit: 'cover',
                    borderRadius: '8px'
                  }}
                />
              )}
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <h3 style={{ fontSize: '22px', fontWeight: 'bold', color: '#1e293b', margin: '0' }}>
                  {devis.mini_pelle_name}
                </h3>
                <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#f97316', marginTop: '10px' }}>
                  {formatPrice(devis.mini_pelle_price)}
                </p>
                <p style={{ fontSize: '12px', color: '#64748b' }}>Prix de base HT</p>
              </div>
            </div>
          </div>

          {devis.options && devis.options.length > 0 && (
            <div style={{ marginBottom: '30px' }}>
              <h2 style={{
                fontSize: '18px',
                fontWeight: 'bold',
                color: '#1e293b',
                borderBottom: '2px solid #f97316',
                paddingBottom: '8px',
                marginBottom: '15px'
              }}>
                OPTIONS ET ACCESSOIRES ({devis.options.length})
              </h2>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '10px'
              }}>
                {devis.options.map((opt, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    backgroundColor: '#f8fafc',
                    padding: '10px',
                    borderRadius: '6px'
                  }}>
                    {getImageUrl(opt.image_url) && (
                      <img
                        src={getImageUrl(opt.image_url)}
                        alt={opt.option_name}
                        style={{
                          width: '50px',
                          height: '50px',
                          objectFit: 'cover',
                          borderRadius: '4px'
                        }}
                      />
                    )}
                    <div>
                      <p style={{ fontSize: '12px', fontWeight: '600', color: '#1e293b', margin: '0' }}>
                        {opt.option_name}
                      </p>
                      <p style={{
                        fontSize: '14px',
                        fontWeight: 'bold',
                        color: (opt.price === 0 || opt.included) ? '#16a34a' : '#f97316',
                        margin: '0'
                      }}>
                        {(opt.price === 0 || opt.included) ? '✓ Inclus' : formatPrice(opt.price)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {devis.notes && (
            <div style={{
              marginBottom: '30px',
              backgroundColor: '#fef3c7',
              border: '1px solid #fcd34d',
              padding: '15px',
              borderRadius: '8px'
            }}>
              <p style={{ fontSize: '12px', fontWeight: 'bold', color: '#92400e', marginBottom: '5px' }}>
                NOTES
              </p>
              <p style={{ color: '#78350f', margin: '0' }}>{devis.notes}</p>
            </div>
          )}

          <div style={{
            borderTop: '2px solid #e2e8f0',
            paddingTop: '20px',
            marginBottom: '30px'
          }}>
            <div style={{ maxWidth: '350px', marginLeft: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', color: '#64748b' }}>
                <span>Machine</span>
                <span style={{ fontFamily: 'monospace' }}>{formatPrice(devis.mini_pelle_price)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', color: '#64748b' }}>
                <span>Options</span>
                <span style={{ fontFamily: 'monospace' }}>{formatPrice(devis.total_options)}</span>
              </div>
              <div style={{ borderTop: '1px solid #e2e8f0', marginTop: '8px', paddingTop: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                  <span style={{ color: '#64748b' }}>Sous-total HT</span>
                  <span style={{ fontFamily: 'monospace', fontWeight: '600' }}>{formatPrice(devis.subtotal)}</span>
                </div>
              </div>
              {devis.discount_amount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', color: '#16a34a' }}>
                  <span>Remise</span>
                  <span style={{ fontFamily: 'monospace' }}>-{formatPrice(devis.discount_amount)}</span>
                </div>
              )}
              <div style={{
                backgroundColor: '#1e293b',
                color: 'white',
                padding: '15px',
                borderRadius: '8px',
                marginTop: '10px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span style={{ fontSize: '18px', fontWeight: 'bold' }}>TOTAL TTC</span>
                <span style={{ fontSize: '24px', fontWeight: 'bold', fontFamily: 'monospace' }}>
                  {formatPrice(devis.total)}
                </span>
              </div>
            </div>
          </div>

          <div style={{
            border: '2px solid #fed7aa',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <div style={{
              backgroundColor: '#f97316',
              color: 'white',
              padding: '12px 15px',
              fontWeight: 'bold',
              fontSize: '16px'
            }}>
              SIMULATION FINANCEMENT SNC
            </div>
            <div style={{ backgroundColor: '#fff7ed', padding: '20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px' }}>
                <div style={{ backgroundColor: 'white', padding: '15px', borderRadius: '8px', border: '1px solid #fed7aa' }}>
                  <p style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', marginBottom: '5px' }}>
                    Apport SNC (30%)
                  </p>
                  <p style={{ fontSize: '18px', fontWeight: 'bold', fontFamily: 'monospace', color: '#1e293b', margin: '0' }}>
                    {formatPrice(snc.apportSNC)}
                  </p>
                </div>
                <div style={{ backgroundColor: 'white', padding: '15px', borderRadius: '8px', border: '1px solid #fed7aa' }}>
                  <p style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', marginBottom: '5px' }}>
                    Apport client ({apportClientPercent}%)
                  </p>
                  <p style={{ fontSize: '18px', fontWeight: 'bold', fontFamily: 'monospace', color: '#1e293b', margin: '0' }}>
                    {formatPrice(snc.apportClient)}
                  </p>
                </div>
                <div style={{ backgroundColor: 'white', padding: '15px', borderRadius: '8px', border: '1px solid #fed7aa' }}>
                  <p style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', marginBottom: '5px' }}>
                    Montant financé
                  </p>
                  <p style={{ fontSize: '18px', fontWeight: 'bold', fontFamily: 'monospace', color: '#1e293b', margin: '0' }}>
                    {formatPrice(snc.totalFinance)}
                  </p>
                </div>
                <div style={{ backgroundColor: '#f97316', padding: '15px', borderRadius: '8px', color: 'white' }}>
                  <p style={{ fontSize: '11px', textTransform: 'uppercase', marginBottom: '5px', opacity: '0.9' }}>
                    Loyer mensuel (60 mois)
                  </p>
                  <p style={{ fontSize: '18px', fontWeight: 'bold', fontFamily: 'monospace', margin: '0' }}>
                    {formatPrice(snc.loyerMensuel)}/mois
                  </p>
                </div>
              </div>
              <p style={{ fontSize: '10px', color: '#64748b', marginTop: '15px', fontStyle: 'italic' }}>
                * Simulation indicative basée sur un financement SNC sur 60 mois avec coefficient 1.2. Apport total: {formatPrice(snc.totalApport)} ({25 + apportClientPercent}%). Sous réserve d'acceptation du dossier.
              </p>
            </div>
          </div>
        </>
      )}

      {/* Footer */}
      <div style={{ 
        marginTop: '40px', 
        paddingTop: '20px', 
        borderTop: '1px solid #e2e8f0',
        textAlign: 'center',
        color: '#94a3b8',
        fontSize: '11px'
      }}>
        <p>Document généré le {new Date().toLocaleDateString('fr-FR')} | MINI PELLE CRM</p>
      </div>
    </div>
  );
};

export default DevisDetailPage;
